import { ApiResponse, PaginatedResponse } from '@/domain';
import {
  Agreement,
  AgreementDocument,
  AgreementFilters,
  CreateAgreementData,
  CremationData,
  CremationFilters,
  SubscriptionData,
  SubscriptionFilters,
  UpdateAgreementData
} from '@/domain/agreements/agreement';
import { PaymentPlan } from '@/domain/agreements/payment-plan';
import { AssuredPersonRequest } from '@/domain/assured/Assured';
import { DeathData } from '@/domain/deathData/deathData';
import { ParcelData, ParcelState } from '@/domain/parcels/parcel';
import { CreatePaymentTransactionData, PaymentTransaction } from '@/domain/payments/payments';
import { CreateRedemptionRequest } from '@/domain/redeem/redeem';
import { SubscriptionStatus } from '@/domain/subscriptions/subscriptions';
import { backendApi, paramsSerializerOpt } from '../backendApi';

export type CreateAgreementRequest = {
  agreement: Omit<CreateAgreementData, 'deathData' | 'assuredPeople' | 'hasPaymentPlan' | 'installmentAmount' | 'installments'>,
  payment: CreatePaymentTransactionData,
  deathData: DeathData[],
  assuredContract?: {
    people: AssuredPersonRequest[],
  },
  paymentPlan?: PaymentPlan,
  parcel?: ParcelData;
}

export type CreateMultiAgreementRequest = {
  agreements: Omit<CreateAgreementData, 'deathData' | 'assuredPeople' | 'hasPaymentPlan' | 'installmentAmount' | 'installments'>[],
  parcel: ParcelData,
  deathData?: DeathData[],
  updateDeathData?: DeathData[],
  paymentPlan?: PaymentPlan;
}

export interface CreatePaymentForAgreementDto {
  agreementId: string;
  payment: Omit<CreatePaymentTransactionData, 'amount'>;
}

export enum AgreementAction {
  ModifySubscription = 'modify_subscription',
  ModifyStatus = 'modify_status',
  RedeemPeople = 'redeem_people',
  ModifyParcel = 'modify_parcel'
}

export interface ParcelUpdateRequest {
  id: string | null;
  state?: ParcelState;
  newParcelId?: string;
}

export interface AgreementActionRequest {
  contractId: string;
  action: AgreementAction;
  parcel?: ParcelUpdateRequest;
  redeem?: CreateRedemptionRequest;
  status?: string;
  subscriptionStatus?: SubscriptionStatus;
}

const agreementActionMap: Record<AgreementAction, string> = {
  modify_subscription: 'modify_subscription',
  modify_status: 'modify_status',
  redeem_people: 'redeem_people',
  modify_parcel: 'modify_parcel',
};

export const agreementsApi = {
  getAgreements: (filters?: AgreementFilters) =>
    backendApi.get<ApiResponse<PaginatedResponse<Agreement>>>('/api/v1/data/agreements', {
      params: filters,
    }).then((res) => res.data.data),

  getDataSubscriptions: (filters?: SubscriptionFilters) =>
    backendApi.get<ApiResponse<PaginatedResponse<SubscriptionData>>>('/api/v1/data/subscriptions',
      paramsSerializerOpt(filters),
    ).then((res) => res.data.data),

  getDataCremations: (filters?: CremationFilters) =>
    backendApi.get<ApiResponse<PaginatedResponse<CremationData>>>('/api/v1/data/cremations',
      paramsSerializerOpt(filters),
    ).then((res) => res.data.data),

  getAgreement: (id: string) =>
    backendApi.get<ApiResponse<Agreement>>(`/api/v1/data/agreements/${id}`).then((res) => res.data.data),

  createAgreement: (req: CreateAgreementRequest) =>
    backendApi.post<ApiResponse<{ payment: PaymentTransaction, agreement: Agreement, billingPeriodId?: string }>>('/api/v1/orch/agreements', req).then((res) => res.data.data),

  createMultiAgreement: (req: CreateMultiAgreementRequest) =>
    backendApi.post<ApiResponse<{ payments: PaymentTransaction[], billingPeriodId?: string }>>('/api/v1/orch/multi-agreements', req).then((res) => res.data.data),

  createAgreementPayment: (req: CreatePaymentForAgreementDto) =>
    backendApi.post<ApiResponse<{ payment: PaymentTransaction }>>(`/api/v1/orch/agreements/${req.agreementId}/payments`, { payment: req.payment }).then((res) => res.data.data),

  updateAgreement: (id: string, data: UpdateAgreementData) =>
    backendApi.put<ApiResponse<{ agreement: Agreement }>>(`/api/v1/orch/agreements/${id}`, data).then((res) => res.data.data),

  deleteAgreement: (id: string) =>
    backendApi.delete<ApiResponse<void>>(`/api/v1/orch/agreements/${id}`).then((res) => res.data.data),

  uploadDocument: (agreementId: string, file: File) => {
    const formData = new FormData()
    formData.append('document', file)
    return backendApi.post<ApiResponse<AgreementDocument>>(
      `/api/v1/agreements/${agreementId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    ).then((res) => res.data.data)
  },

  deleteDocument: (agreementId: string, documentId: string) =>
    backendApi.delete<ApiResponse<void>>(`/api/v1/agreements/${agreementId}/documents/${documentId}`).then((res) => res.data.data),

  getAgreementsByClient: (clientId: string) =>
    backendApi.get<ApiResponse<Agreement[]>>(`/api/v1/clients/${clientId}/agreements`).then((res) => res.data.data),

  getAgreementDocuments: (agreementId: string) =>
    backendApi.get<ApiResponse<AgreementDocument[]>>(`/api/v1/agreements/${agreementId}/documents`).then((res) => res.data.data),

  getAgreementStats: (filters?: AgreementFilters) =>
    backendApi.get<ApiResponse<{
      total: number
      active: number
      completed: number
      totalRevenue: number
      totalOutstanding: number
      averageAmount: number
    }>>('/api/v1/data/agreements/stats', {
      params: filters,
    }).then((res) => res.data.data),

  getLatestContractNumber: (serviceType: string) =>
    backendApi.get<ApiResponse<string | null>>(`/api/v1/agreements/latest/${serviceType}`).then((res) => res.data.data),

  postAgreementAction: (data: AgreementActionRequest) =>
    backendApi.put<ApiResponse<void>>(`/api/v1/orch/agreements/${data.contractId}/actions`, data).then((res) => res.data.data)

}
